import i18n from 'i18next';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CampoControlado, mesclarVazios, type DefCampo } from '../../componentes/formulario';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { ErroRequisicao, mensagemDeErro } from '../../nucleo/api';

const LARGURAS = { inteira: 'sm:col-span-6', meia: 'sm:col-span-3', terco: 'sm:col-span-2' };

/** Estado de formulário com valores à vista (para prévia ao vivo) e erros do servidor por campo. */
export function useEstadoForm<T extends Record<string, unknown>>(inicial: T) {
  const [valores, setValores] = useState<T>(inicial);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  useValidacaoLocal(erros, erroGeral);

  const definir = (nome: string, v: unknown) => setValores((x) => ({ ...x, [nome]: v }));
  /** Lote 19/10 (item 22): o que a consulta do CNPJ ou do CEP trouxe entra só nos campos vazios (nunca sobre o digitado). */
  const mesclar = (dados: Record<string, unknown>) => setValores((x) => mesclarVazios(x, dados) as T);

  /** Valida o obrigatório no cliente (mensagem `erros.obrigatorio`; o 3º argumento é legado e ignorado) e chama `salvar`; erro do servidor volta para o campo certo. */
  // rl0311-3 ROB3-B2: duplo clique não manda de novo enquanto o primeiro envio não voltou.
  const emEnvio = useRef(false);
  async function enviar(e: FormEvent, defs: DefCampo[], _legado: string, salvar: (v: T) => Promise<unknown>) {
    e.preventDefault();
    if (emEnvio.current) return;
    const locais: Record<string, string> = {};
    for (const c of defs) {
      if (c.visivel && !c.visivel(valores)) continue;
      const v = valores[c.nome];
      if (c.obrigatorio && c.tipo !== 'booleano' && (v === undefined || v === null || v === '')) locais[c.nome] = i18n.t('erros.obrigatorio');
    }
    setErros(locais);
    setErroGeral(null);
    if (Object.keys(locais).length) return;
    setEnviando(true);
    emEnvio.current = true;
    try {
      await salvar(valores);
    } catch (err) {
      if (err instanceof ErroRequisicao) {
        const porCampo = err.porCampo();
        setErros(porCampo);
        if (!defs.some((c) => porCampo[c.nome])) setErroGeral(mensagemDeErro(err));
      } else setErroGeral(mensagemDeErro(err));
    } finally {
      emEnvio.current = false;
      setEnviando(false);
    }
  }

  return { valores, definir, mesclar, erros, erroGeral, enviando, enviar };
}

/** Grade de campos do núcleo (`CampoControlado`) ligada ao estado do formulário. */
export function CamposForm({ defs, valores, erros, erroGeral, aoMudar, children }: { defs: DefCampo[]; valores: Record<string, unknown>; erros: Record<string, string>; erroGeral: string | null; aoMudar: (nome: string, v: unknown) => void; children?: ReactNode }) {
  return (
    <>
      {erroGeral && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
          {erroGeral}
        </p>
      )}
      {defs
        .filter((c) => !c.visivel || c.visivel(valores))
        .map((c) => (
          <div key={c.nome} className={LARGURAS[c.largura ?? 'inteira']}>
            <CampoControlado def={c} valor={valores[c.nome]} erro={erros[c.nome]} aoMudar={(v) => aoMudar(c.nome, v)} />
          </div>
        ))}
      {children}
    </>
  );
}

export const GRADE_FORM = 'grid grid-cols-1 gap-4 sm:grid-cols-6';
